import has from 'lodash/has';
import get from 'lodash/get';
import { FILTER_COLOR_SWATCH_URL } from '../../ProductListing/container/ProductListing.constants';
import {
  CLOTHING_SIZE_FILTER,
  MIN_OFFER_PRICE,
  SIZE_FILTER,
} from '../constants/QuickFilters.constants';

export function getColorFilterOptionsMap(colorOptionsMap, facetName) {
  return colorOptionsMap?.map((color) => {
    const imgUrl = `${FILTER_COLOR_SWATCH_URL}${color.id}.gif`;
    const imgData = { alt: color.displayName, url: imgUrl };
    const imgConfig = `w_50,h_50,c_thumb,g_auto:0`;
    const imgDataConfig = [`${imgConfig}`, `${imgConfig}`, `${imgConfig}`];
    const whiteColorClass = color.displayName?.toLowerCase() === 'white';
    return {
      value: color.id,
      title: color.displayName,
      imageData: imgData,
      imageConfig: imgDataConfig,
      colorName: color.displayName.toLowerCase(),
      name: facetName,
      class: `${whiteColorClass ? 'white-color' : ''}`,
    };
  });
}

export const extractAppliedFilters = (appliedFilter = {}, facetName) => {
  return (has(appliedFilter, facetName) && get(appliedFilter, facetName)) || [];
};

export const getAppliedFiltersArray = (appliedFilters = {}) => {
  const appliedFiltersArray = [];
  Object.keys(appliedFilters)?.forEach((filter) => {
    if (filter === 'min_offer_price') {
      appliedFiltersArray.push({
        id: appliedFilters[filter][0],
        displayName: appliedFilters[filter][0],
        facetName: filter,
      });
    }
  });
  return appliedFiltersArray;
};

export const priceRangeNumFormatter = (num) =>
  typeof num === 'number' ? num?.toFixed(2) : parseFloat(num)?.toFixed(2);

export const mergeFitAndSizeFilters = (filterMaps = {}) => {
  let filters = JSON.parse(JSON.stringify(filterMaps));

  filters = Object.assign(filters, filterMaps, {
    fitAndSize: {
      v_tcpfit_unbxd_uFilter: filterMaps?.v_tcpfit_unbxd_uFilter,
      v_tcpsize_uFilter: filterMaps?.v_tcpsize_uFilter,
    },
  });

  return filters;
};

export const isShowQuickFilterTabs = (facetName, tabs) => {
  return (
    (facetName === CLOTHING_SIZE_FILTER || facetName === SIZE_FILTER) &&
    Array.isArray(tabs) &&
    tabs.length
  );
};

export const getPriceFromAppliedFilter = (appliedRange, priceOptions) => {
  if (!appliedRange) return { min: priceOptions?.pMinInitial, max: priceOptions?.pMaxInitial };
  if (Array.isArray(appliedRange) && appliedRange.length) {
    if (typeof appliedRange[0] === 'string') {
      const [min, max] = appliedRange[0]?.split('TO');
      return { min: parseFloat(min), max: parseFloat(max) };
    }
    const [min, max] = appliedRange[0]?.id?.split('TO');
    return { min: parseFloat(min), max: parseFloat(max) };
  }
  const [min, max] = appliedRange?.id?.split('TO');
  return { min: parseFloat(min), max: parseFloat(max) };
};

export const getAppliedPriceFilter = (appliedFilters, priceOptions) => {
  if (Array.isArray(appliedFilters) && appliedFilters.length) {
    const appliedPriceFilter = appliedFilters?.filter((filterMaps) => {
      return filterMaps?.some((filter) => {
        return filter?.facetName === MIN_OFFER_PRICE;
      });
    });
    if (!appliedPriceFilter)
      return { min: priceOptions?.pMinInitial, max: priceOptions?.pMaxInitial };
    return getPriceFromAppliedFilter(appliedPriceFilter[0]);
  }
  return getPriceFromAppliedFilter(get(appliedFilters, MIN_OFFER_PRICE), priceOptions);
};

export const getAppliedFilterCount = (appliedFilters, facetName) => {
  if (!appliedFilters || !facetName) return 0;
  if (!has(appliedFilters, facetName)) return 0;
  return get(appliedFilters, facetName)?.length;
};

export const getAllAppliedFiltersCount = (appliedFilters) => {
  if (!Array.isArray(appliedFilters)) {
    return Object.keys(appliedFilters).filter((facet) => appliedFilters[facet]?.length).length;
  }
  return 0;
};

export const getMergeUnCommonQuickFilters = (quickFilters, correspondingFacets) => {
  const result = {};
  // Get the keys from Obj1
  const obj1Keys = Object.keys(correspondingFacets);
  // Loop through Obj1 keys
  obj1Keys.forEach((key1) => {
    // corresponding factes -> sidebar -> _ufilter
    const baseKey1 = key1.split('_uFilter')[0]; // Get the base key (e.g., "TCPColor")
    // Check if any key in Obj2 contains the base key
    const matchingKeys = Object.keys(quickFilters).filter((key2) => key2.includes(baseKey1));
    // If a match is found, include Obj1's key-value pair in the result
    if (matchingKeys?.length > 0) {
      result[key1] = correspondingFacets[key1];
    }
  });
  // Now include all key-value pairs from Obj2 that do not match any keys in Obj1
  Object.keys(quickFilters).forEach((key2) => {
    if (!obj1Keys.some((key1) => key2.includes(key1.split('_uFilter')[0]))) {
      result[key2] = quickFilters[key2];
    }
  });
  return result;
};

export const getCorrespondingFacets = (quickFilters, sidebarFilters, obj, filterParam) => {
  const quickFilterModuleObjkeys = [];
  Object.keys(quickFilters)?.forEach(
    (key) => quickFilterModuleObjkeys.push(key.split(filterParam)[0]) // quick filter module obj keys.
  );
  const correspondingFacets = {};
  quickFilterModuleObjkeys?.forEach((key) => {
    // quick filter key.
    Object.keys(sidebarFilters).forEach((keyNew) => {
      // sidebar filter key.
      if (keyNew?.toLowerCase().includes(key?.toLowerCase())) {
        correspondingFacets[keyNew] = obj[keyNew]; // copying the corresponding keys (sidebar U quickFilter)
      }
    });
  });
  return correspondingFacets;
};

const modifyKeys = (obj, sidebarFilterObj) => {
  // Get the base names from the second object keys
  const baseNames = Object.keys(sidebarFilterObj).map((key) => key.split('_')[0]);
  return Object.entries(obj).reduce((acc, [key, value]) => {
    // Extract the base name from the current key
    const baseName = key.split('_')[0];
    // Check if the base name is in the baseNames array
    if (baseNames.includes(baseName)) {
      // Replace "_M", "_H", "_h", "_m" with an empty string
      const newKey = key.replace(/_M|_H|_h|_m/g, '');
      acc[newKey] = value;
    } else {
      // If not modifying, keep the original key
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const getQuickFilters = (quickFilters, sidebarFilters, obj, filterParam) => {
  const correspondingFacets = getCorrespondingFacets(
    quickFilters,
    sidebarFilters,
    obj,
    filterParam
  );
  const result = getMergeUnCommonQuickFilters(quickFilters, correspondingFacets);
  return modifyKeys(result, sidebarFilters);
};
